import { useMemo, useState, type ReactNode } from "react";
import type {
  OrgSeatClaudeModel,
  OrgSeatClaudeSource,
  OrgSeatDetailResponse,
  OrgSeatDetailSubscription,
  OrgSeatModelKind,
  OrgSeatRosterMember,
  OrgSeatRosterSubscription,
  OrgSeatState,
  OrgSeatWindowUsage,
} from "@dosia/shared";
import { StatusPill } from "../../components/StatusPill.js";
import { SecondaryButton, TextInput } from "../../components/FormField.js";
import { Modal } from "../../components/Modal.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { modelsSeatsPath, modelSeatDetailPath } from "../../lib/api-paths.js";
import { cn } from "../../lib/cn.js";

/**
 * 席位总览 tab —— 回答「企业给某员工买的 Claude 订阅席位，客户端到底有没有真用上，
 * 还是配置全绿、钱在闲置、模型仍走基础额度」。全员一行一人的席位裁决 + 订阅健康度。
 *
 * 数据契约的权威 typed 定义单一真相源在 `@dosia/shared`（`OrgSeat*`，红线 #9）：
 * producer = apps/web seat-diagnostics.ts，consumer = 本页，禁两处手搓。本页只做只读消费，
 * 在 normalize 边界对不可信输入（读盘/网络/CLI 输出）做运行时校验 + 未知枚举安全降级，
 * 使其满足 shared 的 typed shape 后再进入渲染（union 字段未知值给安全默认：claudeSource→none、
 * kind→base；reason 在 shared 里就是 string，翻译层未知→「其他」）。
 *
 * 内部名脱敏由服务端保证（响应只给 subscription/base/none 等 kind 枚举，不含 claude-lite 等
 * 原始渠道名）；UI 不凭空拼原始名。
 */

// ---------- 边界防御原语（不可信输入面：读盘/网络/CLI 输出） ----------

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** union 字段安全收敛：未知值给安全默认，满足 shared typed shape（不 throw）。 */
function claudeSourceOf(v: unknown): OrgSeatClaudeSource {
  return v === "subscription" || v === "base" || v === "none" ? v : "none";
}
function modelKindOf(v: unknown): OrgSeatModelKind {
  return v === "subscription" || v === "base" ? v : "base";
}
function statusOf(v: unknown): "active" | "expired" {
  return v === "expired" ? "expired" : "active";
}

function normDecision(v: unknown): OrgSeatState {
  const o = obj(v);
  return {
    effective: o?.effective === true,
    claudeSource: claudeSourceOf(o?.claudeSource),
    reason: str(o?.reason),
  };
}

function normRosterSubscription(v: unknown): OrgSeatRosterSubscription | null {
  const o = obj(v);
  if (!o) return null;
  return {
    tierName: str(o.tierName) ?? "",
    status: statusOf(o.status),
    expiresAt: str(o.expiresAt),
    expiresInDays: num(o.expiresInDays),
  };
}

function normWindow(v: unknown): OrgSeatWindowUsage | null {
  const o = obj(v);
  if (!o) return null;
  const limitUsd = num(o.limitUsd);
  const usedUsd = num(o.usedUsd);
  const percentage = num(o.percentage);
  if (limitUsd === null || usedUsd === null || percentage === null) return null;
  return { limitUsd, usedUsd, percentage, resetsAt: str(o.resetsAt), resetsInSeconds: num(o.resetsInSeconds) };
}

function normRosterMembers(data: unknown): OrgSeatRosterMember[] {
  const root = obj(data);
  const raw = root?.members;
  if (!Array.isArray(raw)) return [];
  const out: OrgSeatRosterMember[] = [];
  for (const item of raw) {
    const o = obj(item);
    const employeeId = str(o?.employeeId);
    if (!o || employeeId === null) continue; // 无 id 的畸形行直接跳过
    out.push({
      employeeId,
      online: o.online === true,
      lastFetchedAt: str(o.lastFetchedAt),
      subscription: normRosterSubscription(o.subscription),
      seat: normDecision(o.seat),
    });
  }
  return out;
}

function normClaudeModels(v: unknown): OrgSeatClaudeModel[] {
  if (!Array.isArray(v)) return [];
  const out: OrgSeatClaudeModel[] = [];
  for (const item of v) {
    const o = obj(item);
    const modelId = str(o?.modelId);
    if (!o || modelId === null) continue;
    const chosen = obj(o.chosen);
    const shadowedRaw = o.shadowed;
    const shadowed = Array.isArray(shadowedRaw)
      ? shadowedRaw.map((s) => {
          const so = obj(s);
          return { kind: modelKindOf(so?.kind), reason: str(so?.reason) ?? "" };
        })
      : [];
    out.push({ modelId, chosen: { kind: modelKindOf(chosen?.kind) }, shadowed });
  }
  return out;
}

function normDetail(data: unknown, fallbackEmployeeId: string): OrgSeatDetailResponse {
  const o = obj(data);
  const sub = obj(o?.subscription);
  let subscription: OrgSeatDetailSubscription | null = null;
  if (sub) {
    const win = obj(sub.windows);
    subscription = {
      tierName: str(sub.tierName) ?? "",
      status: statusOf(sub.status),
      startsAt: str(sub.startsAt),
      expiresAt: str(sub.expiresAt),
      expiresInDays: num(sub.expiresInDays),
      windows: {
        daily: normWindow(win?.daily),
        weekly: normWindow(win?.weekly),
        monthly: normWindow(win?.monthly),
      },
    };
  }
  return {
    employeeId: str(o?.employeeId) ?? fallbackEmployeeId,
    subscription,
    seat: normDecision(o?.seat),
    claudeModels: normClaudeModels(o?.claudeModels),
  };
}

// ---------- 枚举 → 中文（集中映射；未知降级「其他」，禁露原始英文枚举给用户） ----------

const KIND_LABEL: Record<string, string> = {
  subscription: "订阅席位",
  base: "基础额度",
  none: "无",
};
function kindLabel(kind: string | null): string {
  if (!kind) return "无";
  return KIND_LABEL[kind] ?? "其他";
}

const SEAT_REASON_LABEL: Record<string, string> = {
  subscription_expired: "订阅已过期，已回落基础额度",
  no_active_account: "订阅组无可用账号",
  missing_credential: "订阅组缺下发密钥",
  shadowed_by_higher: "被更高优先级来源盖过",
  // not_entitled → 无订阅，不显示 reason
};
function seatReasonLabel(reason: string | null): string | null {
  if (!reason || reason === "not_entitled") return null;
  return SEAT_REASON_LABEL[reason] ?? "其他";
}

const SHADOWED_REASON_LABEL: Record<string, string> = {
  non_subscription: "订阅席位优先",
  priority_lower: "优先级更低",
  sort_order_lower: "排序靠后",
  group_id_higher: "默认次序靠后",
};
function shadowedReasonLabel(reason: string | null): string {
  if (!reason) return "其他";
  return SHADOWED_REASON_LABEL[reason] ?? "其他";
}

// ---------- 格式化 ----------

function formatDate(iso: string | null): string {
  // 契约给 ISO 串；截前 10 位得 YYYY-MM-DD（与参考稿一致，避免 zh-CN 的斜杠格式）。
  if (typeof iso === "string" && iso.length >= 10) return iso.slice(0, 10);
  return "—";
}

function formatDateTime(iso: string | null): string {
  if (typeof iso !== "string") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("zh-CN");
}

function formatMoney(n: number): string {
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
}

function formatResetIn(seconds: number | null): string | null {
  if (seconds === null || seconds <= 0) return null;
  const hours = seconds / 3600;
  if (hours >= 24) return `${Math.round(hours / 24)} 天后重置`;
  return `${Math.round(hours)} 小时后重置`;
}

function isExpired(sub: { status: "active" | "expired" } | null): boolean {
  return sub?.status === "expired";
}

// ---------- 小组件 ----------

/** accent 淡药丸（会员档位 / 生效来源=订阅席位）。用 dosia-accent token，非硬编码色。 */
function AccentPill({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded-full border border-dosia-accent/40 bg-dosia-accent/10 px-2 py-0.5 text-xs font-medium text-dosia-text">
      {children}
    </span>
  );
}

// ---------- 详情 Modal ----------

function WindowBar({ label, win }: { label: string; win: OrgSeatWindowUsage }): JSX.Element {
  const pct = Math.max(0, Math.min(100, win.percentage));
  const over = win.percentage >= 80;
  const resetIn = formatResetIn(win.resetsInSeconds);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-dosia-text-secondary">{label}</span>
        <span className="font-mono text-dosia-text">
          {formatMoney(win.usedUsd)} / {formatMoney(win.limitUsd)}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-dosia-bg-lighter">
        <div
          className={cn("h-full rounded-full", over ? "bg-amber-500" : "bg-dosia-accent")}
          style={{ width: `${pct}%` }}
        />
      </div>
      {resetIn ? <div className="text-[10px] text-dosia-text-dim">{resetIn}</div> : null}
    </div>
  );
}

function SubscriptionHealthCard({ sub }: { sub: OrgSeatDetailSubscription | null }): JSX.Element {
  if (!sub) {
    return (
      <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-3 text-sm text-dosia-text-secondary">
        该员工无订阅席位。
      </div>
    );
  }
  const expired = isExpired(sub);
  const windows: Array<[string, OrgSeatWindowUsage | null]> = [
    ["今日", sub.windows.daily],
    ["本周", sub.windows.weekly],
    ["本月", sub.windows.monthly],
  ];
  const shownWindows = windows.filter((entry): entry is [string, OrgSeatWindowUsage] => entry[1] !== null);
  return (
    <div className="space-y-3 rounded-lg border border-dosia-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-dosia-text">订阅健康度</span>
        <div className="flex items-center gap-2">
          {sub.tierName ? <AccentPill>{sub.tierName}</AccentPill> : null}
          {expired ? (
            <StatusPill status="disconnected" label="已过期" />
          ) : sub.expiresAt ? (
            <span className="text-xs text-dosia-text-dim">
              到期 {formatDate(sub.expiresAt)}
              {sub.expiresInDays !== null ? ` · 剩 ${sub.expiresInDays} 天` : ""}
            </span>
          ) : null}
        </div>
      </div>
      {shownWindows.length > 0 ? (
        <div className="space-y-3">
          {shownWindows.map(([label, w]) => (
            <WindowBar key={label} label={label} win={w} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-dosia-text-dim">暂无订阅用量窗口数据。</p>
      )}
      <p className="text-[10px] leading-relaxed text-dosia-text-dim">
        额度窗口为滚动窗口（非自然日/周/月），数据来自订阅进度接口，与员工客户端一致。
      </p>
    </div>
  );
}

function ClaudeSourceTable({ models }: { models: OrgSeatClaudeModel[] }): JSX.Element {
  if (models.length === 0) {
    return <p className="text-sm text-dosia-text-dim">暂无 Claude 模型数据。</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-dosia-border text-left text-xs text-dosia-text-dim">
            <th className="py-1.5 pr-3 font-medium">模型</th>
            <th className="py-1.5 pr-3 font-medium">生效来源</th>
            <th className="py-1.5 pr-3 font-medium">竞争来源 · 落选原因</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m.modelId} className="border-b border-dosia-border/50 align-top">
              <td className="py-1.5 pr-3 font-mono text-dosia-text">{m.modelId}</td>
              <td className="py-1.5 pr-3">
                {m.chosen.kind === "subscription" ? (
                  <div className="flex flex-wrap items-center gap-1">
                    <AccentPill>订阅席位</AccentPill>
                    <StatusPill status="connected" label="✓ 走订阅" />
                  </div>
                ) : (
                  <StatusPill status="archived" label={kindLabel(m.chosen.kind)} />
                )}
              </td>
              <td className="py-1.5 pr-3">
                {m.shadowed.length > 0 ? (
                  <div className="flex flex-col gap-0.5">
                    {m.shadowed.map((s, i) => (
                      <span key={i} className="text-xs text-dosia-text-secondary">
                        <del className="text-dosia-text-dim">{kindLabel(s.kind)}</del>
                        {" · "}
                        <span>{shadowedReasonLabel(s.reason)}</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-dosia-text-dim">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeatDetailModal({
  orgCode,
  employeeId,
  onClose,
}: {
  orgCode: string;
  employeeId: string;
  onClose: () => void;
}): JSX.Element {
  const detailQ = useApiQuery<unknown>(modelSeatDetailPath(orgCode, employeeId), [orgCode, employeeId]);
  const detail = useMemo(() => normDetail(detailQ.data, employeeId), [detailQ.data, employeeId]);
  const reasonLabel = seatReasonLabel(detail.seat.reason);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`${employeeId} · 模型来源详情`}
      footer={<SecondaryButton onClick={onClose}>关闭</SecondaryButton>}
    >
      <div className="space-y-4">
        <p className="text-xs text-dosia-text-dim">
          <span className="font-mono">{employeeId}</span> · 实时计算，与员工客户端收到的配置一致。
        </p>
        {detailQ.error ? <ErrorBanner>{detailQ.error.message}</ErrorBanner> : null}
        {detailQ.loading && !detailQ.data ? (
          <p className="text-sm text-dosia-text-dim">加载中…</p>
        ) : (
          <>
            {detail.seat.effective ? (
              <div className="rounded-md border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-700">
                订阅席位正在生效。
              </div>
            ) : reasonLabel ? (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
                {reasonLabel}
              </div>
            ) : null}
            <SubscriptionHealthCard sub={detail.subscription} />
            <div className="space-y-2">
              <span className="text-sm font-medium text-dosia-text">Claude 模型来源</span>
              <ClaudeSourceTable models={detail.claudeModels} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------- roster 主视图 ----------

function TierCell({ sub }: { sub: OrgSeatRosterSubscription | null }): JSX.Element {
  if (!sub) return <StatusPill status="archived" label="无" />;
  if (isExpired(sub)) return <StatusPill status="disconnected" label="已过期" />;
  return <AccentPill>{sub.tierName || "订阅"}</AccentPill>;
}

function ExpiryCell({ sub }: { sub: OrgSeatRosterSubscription | null }): JSX.Element {
  if (!sub) return <span className="text-dosia-text-dim">—</span>;
  if (isExpired(sub)) return <span className="text-amber-700">已过期</span>;
  return (
    <div className="flex flex-col">
      <span className="font-mono text-dosia-text">{formatDate(sub.expiresAt)}</span>
      {sub.expiresInDays !== null ? (
        <span className="text-xs text-dosia-text-dim">剩 {sub.expiresInDays} 天</span>
      ) : null}
    </div>
  );
}

function SeatEffectiveCell({ seat }: { seat: OrgSeatState }): JSX.Element {
  if (seat.effective) return <StatusPill status="connected" label="生效中" />;
  const reasonLabel = seatReasonLabel(seat.reason);
  if (reasonLabel) return <StatusPill status="disconnected" label={reasonLabel} />;
  return <StatusPill status="archived" label="无席位" />;
}

export function SeatOverviewTab({ orgCode }: { orgCode: string }): JSX.Element {
  const rosterQ = useApiQuery<unknown>(modelsSeatsPath(orgCode), [orgCode]);
  const members = useMemo(() => normRosterMembers(rosterQ.data), [rosterQ.data]);

  const [search, setSearch] = useState("");
  const [onlyIneffective, setOnlyIneffective] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  // 已购订阅但席位未生效（订阅费闲置）的成员数 —— banner 统计口径。
  const idleCount = useMemo(
    () => members.filter((m) => m.subscription !== null && !m.seat.effective).length,
    [members],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (q && !m.employeeId.toLowerCase().includes(q)) return false;
      if (onlyIneffective && !(m.subscription !== null && !m.seat.effective)) return false;
      return true;
    });
  }, [members, search, onlyIneffective]);

  return (
    <div className="flex flex-col gap-3">
      {rosterQ.error ? <ErrorBanner>{rosterQ.error.message}</ErrorBanner> : null}

      {/* 顶部提示 banner：有闲置席位 → amber；全部生效 → 绿；无成员 → 不显示 */}
      {idleCount > 0 ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
          {idleCount} 位员工已购订阅席位但客户端未用上，他们的 Claude 模型仍在走基础额度，订阅费处于闲置状态。点击对应员工行查看裁决细节。
        </div>
      ) : members.length > 0 ? (
        <div className="rounded-md border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-700">
          全部订阅席位均已生效。
        </div>
      ) : null}

      {/* 工具条 */}
      <div className="flex flex-wrap items-center gap-2">
        <TextInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜索员工 ID"
          aria-label="搜索员工"
          className="w-48 font-mono"
        />
        <SecondaryButton
          onClick={() => setOnlyIneffective((v) => !v)}
          aria-pressed={onlyIneffective}
          className={cn(
            onlyIneffective && "border-dosia-accent bg-dosia-accent/10 text-dosia-text",
          )}
        >
          仅看订阅未生效
        </SecondaryButton>
        <span className="ml-auto text-xs text-dosia-text-dim">
          共 {members.length} 位已注册成员 · 实时计算
        </span>
      </div>

      {/* roster 表（手写 MembersTable 范式，行可点击） */}
      {rosterQ.loading && !rosterQ.data ? (
        <p className="text-sm text-dosia-text-dim">加载中…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-dosia-text-dim">
          {members.length === 0 ? "暂无已注册成员。" : "没有符合条件的成员。"}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dosia-border text-left text-xs text-dosia-text-dim">
                <th className="py-1.5 pr-3 font-medium">员工</th>
                <th className="py-1.5 pr-3 font-medium">会员档位</th>
                <th className="py-1.5 pr-3 font-medium">订阅到期</th>
                <th className="py-1.5 pr-3 font-medium">席位生效</th>
                <th className="py-1.5 pr-3 font-medium">Claude 模型来源</th>
                <th className="py-1.5 pr-3 font-medium">状态</th>
                <th className="py-1.5 pr-3 font-medium">最近同步</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr
                  key={m.employeeId}
                  onClick={() => setSelected(m.employeeId)}
                  className="cursor-pointer border-b border-dosia-border/50 hover:bg-dosia-hover"
                >
                  <td className="py-1.5 pr-3 font-mono text-dosia-text">{m.employeeId}</td>
                  <td className="py-1.5 pr-3">
                    <TierCell sub={m.subscription} />
                  </td>
                  <td className="py-1.5 pr-3">
                    <ExpiryCell sub={m.subscription} />
                  </td>
                  <td className="py-1.5 pr-3">
                    <SeatEffectiveCell seat={m.seat} />
                  </td>
                  <td className="py-1.5 pr-3 text-dosia-text-secondary">
                    {kindLabel(m.seat.claudeSource)}
                  </td>
                  <td className="py-1.5 pr-3">
                    {m.online ? (
                      <StatusPill status="connected" label="在线" />
                    ) : (
                      <StatusPill status="disconnected" label="离线" />
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-dosia-text-secondary">
                    {formatDateTime(m.lastFetchedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 图例 */}
      <p className="text-xs text-dosia-text-dim">
        生效中 = 席位已被客户端实际采用 · 未生效 = 已购买但客户端走了基础额度 · 无席位 = 未购买订阅。
      </p>

      {selected ? (
        <SeatDetailModal orgCode={orgCode} employeeId={selected} onClose={() => setSelected(null)} />
      ) : null}
    </div>
  );
}
